import { type AITool } from "@app/_metadata";
import LEOLogo from "@shared-assets/leo-logo.svg";
import { Badge } from "@shared-components/shadcn/badge";
import { api } from "@shared-server/api";
import { cn } from "@shared-utils/ui";
import Image from "next/image";

/**
 * LEO Ident used in TopBar and the About dialog
 *
 * @param props.invert - Light-on-dark colours for the About dialog.
 * @param props.aiTool - The active tool whose name follows the logo.
 * @param props.hideTextOnMobile - Show only the logo image below the sm
 * breakpoint, for the top bar where the controls need the width on phones.
 */

interface IdentProps {
  invert?: boolean;
  aiTool?: AITool;
  hideTextOnMobile?: boolean;
}

export const Ident = (props: IdentProps) => {
  const envQuery = api.utils.env.useQuery();
  const env = envQuery.data;

  /**
   * Render
   */
  return (
    <div
      className={cn("mr-4 flex flex-row justify-start", {
        "text-gray-200": props.invert,
      })}
    >
      <Image
        priority={false}
        className={cn("mr-3 w-[40px] dark:invert", {
          invert: props.invert,
        })}
        src={LEOLogo as string}
        alt="LEO logo"
      />
      <div
        data-dd-privacy="allow"
        className={cn({ "hidden sm:block": props.hideTextOnMobile })}
      >
        <div className="-mb-1 text-xs font-extrabold font-heading mt-1 whitespace-nowrap">
          {"LEO Pharma"}
        </div>
        <span className="text-lg whitespace-nowrap inline-block font-heading">
          {props.aiTool?.meta.name || "LEO AI"}
          {env && env !== "prod" && (
            <EnvironmentBadge
              env={env}
              className="mx-2 hidden md:inline-block"
            />
          )}
        </span>
      </div>
      {/* With the text hidden on phones, the environment still needs a home:
          a small badge beside the logo until the text block returns. */}
      {props.hideTextOnMobile && env && env !== "prod" && (
        <EnvironmentBadge env={env} className="self-center sm:hidden" />
      )}
    </div>
  );
};

/** Non-production environment marker, coloured per environment. */
const EnvironmentBadge = (props: { env: string; className?: string }) => (
  <Badge
    size="sm"
    className={cn("text-white", props.className, {
      "bg-ter-yellow-600": props.env === "test",
      "bg-ter-mint-500": props.env === "dev",
      "bg-slate-500": props.env === "local",
    })}
  >
    {props.env.toUpperCase()}
  </Badge>
);
